import React, { useEffect, useRef, useState } from "react";
import { ExternalLink, BadgePercent, ChevronLeft, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const mediaSrc = (path) => (path && !path.startsWith("http") ? `${API}/api/store/media/${path}` : path);
// Sponsor ad slot — priority-ordered rotator: ads change over in priority order (3/5/10s per ad), with manual arrows
export default function AdSlot({ placement, className = "" }) {
  const [ads, setAds] = useState([]);
  const [idx, setIdx] = useState(0);
  const pinged = useRef(new Set());

  useEffect(() => {
    fetch(`${API}/api/ads/serve?placement=${placement}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        list.sort((a, b) => (a.priority ?? 1) - (b.priority ?? 1));
        setAds(list);
      })
      .catch((err) => console.debug("ad serve skipped", err));
  }, [placement]);

  // Changeover in priority order; each ad controls its own display duration (3/5/10s)
  useEffect(() => {
    if (ads.length < 2) return;
    const secs = [3, 5, 10].includes(ads[idx % ads.length]?.rotate_secs) ? ads[idx % ads.length].rotate_secs : 3;
    const t = setTimeout(() => setIdx((i) => (i + 1) % ads.length), secs * 1000);
    return () => clearTimeout(t);
  }, [idx, ads]);

  // Impression ping once per ad per mount
  useEffect(() => {
    ads.forEach((a) => {
      if (pinged.current.has(a.id)) return;
      pinged.current.add(a.id);
      fetch(`${API}/api/ads/${a.id}/impression`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ placement }),
      }).catch((err) => console.debug("impression ping skipped", err));
    });
  }, [ads, placement]);

  if (!ads.length) return null;
  const active = ads[idx % ads.length];

  const click = async (a) => {
    try {
      const res = await fetch(`${API}/api/ads/${a.id}/click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ placement }),
      });
      const body = await res.json();
      if (body.link_url) window.open(body.link_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.debug("ad click track failed", err);
      if (a.link_url) window.open(a.link_url, "_blank", "noopener,noreferrer");
    }
  };

  const step = (dir) => (e) => {
    e.stopPropagation();
    setIdx((i) => (i + dir + ads.length) % ads.length);
  };

  return (
    <div className={`relative ${className}`} data-testid={`ad-slot-${placement}`}>
      <div key={active.id} className="animate-modal-in">
        <button
          onClick={() => click(active)}
          data-testid={`ad-${placement}-${active.id}`}
          className="w-full max-w-2xl mx-auto block text-left bg-white rounded-3xl border border-[#F5E3B3] shadow-[0_10px_30px_-12px_rgba(217,169,62,0.35)] overflow-hidden hover:-translate-y-0.5 transition-transform duration-200 mb-3 dark:bg-[#273244] dark:border-[#374457]"
        >
          {active.media_kind === "video" && active.media_path ? (
            <video src={mediaSrc(active.media_path)} autoPlay muted loop playsInline className="w-full aspect-[2/1] sm:aspect-[21/9] object-cover" />
          ) : active.media_path ? (
            <img src={mediaSrc(active.media_path)} alt={active.title} className="w-full aspect-[2/1] sm:aspect-[21/9] object-cover" style={{ objectPosition: `${active.focal_x ?? 50}% ${active.focal_y ?? 50}%` }} loading="lazy" />
          ) : null}
          <div className="p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs sm:text-sm font-black font-heading text-[#374151] dark:text-slate-100">{active.title}</p>
              <span className="flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-wider text-[#A08244] bg-[#FFF2D1] border border-[#F5E3B3] px-2 py-0.5 rounded-full shrink-0">
                <BadgePercent className="w-2.5 h-2.5" /> Sponsored
              </span>
            </div>
            {active.description && <p className="text-[11px] text-[#6B7280] dark:text-slate-300 mt-1 leading-relaxed line-clamp-2">{active.description}</p>}
            {active.link_url && (
              <p className="text-[11px] font-extrabold text-[#5CB3DC] mt-1.5 flex items-center gap-1">
                Learn more <ExternalLink className="w-3 h-3" />
              </p>
            )}
          </div>
        </button>
      </div>

      {ads.length > 1 && (
        <div className="flex items-center justify-center gap-2 -mt-2 mb-4" data-testid={`ad-rotator-${placement}`}>
          <button onClick={step(-1)} aria-label="Previous ad" data-testid={`ad-rotator-prev-${placement}`} className="w-7 h-7 rounded-full bg-white border border-[#F5E3B3] flex items-center justify-center text-[#374151] hover:bg-[#FFF2D1] transition-colors shadow-sm">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-2.5 py-1 rounded-full bg-white border border-[#F5E3B3] text-[10px] font-extrabold text-[#A08244] shadow-sm" data-testid={`ad-rotator-count-${placement}`}>
            {(idx % ads.length) + 1}/{ads.length}
          </span>
          <button onClick={step(1)} aria-label="Next ad" data-testid={`ad-rotator-next-${placement}`} className="w-7 h-7 rounded-full bg-white border border-[#F5E3B3] flex items-center justify-center text-[#374151] hover:bg-[#FFF2D1] transition-colors shadow-sm">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
